import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import CommonIcons from "@base/assets/icons";
import { backToTop } from "@base/helpers/common";
import useCheckLogin from "@base/hooks/useCheckLogin";
import { responseUI } from "@base/themes/response";
import CommonStyles from "@components/CommonStyles";
import BookingForm from "@components/AppointmentBooking/BookingForm";
import ChooseRecordStep from "@components/AppointmentBooking/ChooseRecordStep";
import ChooseServiceStep from "@components/AppointmentBooking/ChooseServiceStep";
import ChooseSiteStep from "@components/AppointmentBooking/ChooseSiteStep";
import ChooseTypeStep from "@components/AppointmentBooking/ChooseTypeStep";
import PaymentStep from "@components/AppointmentBooking/PaymentStep";
import { BOOKING_DRAFT_KEY, BookingDraft, BookingStep, emptyDraft, nextStep } from "@base/modules/appointment/regularBookingRules";
import siteService from "@base/modules/site/siteService";
import profileService from "@base/modules/user/profile/profileService";

const STEPS: BookingStep[] = ["TYPE", "RECORD", "SITE", "SERVICE", "BOOKING", "PAYMENT"];

const readDraft = (): BookingDraft | null => {
  try {
    const raw = sessionStorage.getItem(BOOKING_DRAFT_KEY);

    return raw ? (JSON.parse(raw) as BookingDraft) : null;
  } catch (error) {
    return null;
  }
};

const writeDraft = (draft: BookingDraft) => {
  try {
    sessionStorage.setItem(BOOKING_DRAFT_KEY, JSON.stringify(draft));
  } catch (error) {}
};

/**
 * The entry point says what is already known (`dat-lich-thuong-web-spec.md` §6):
 * - a doctor's profile: `type`, `site_code`, `doctor_code`, `specialization_code`, optional branch;
 * - a clinic: `site_code`, optional branch;
 * - a follow-up visit: `type`, `record_code`, `site_code`, `date`, `reappointment=1`;
 * - nothing at all (the empty appointments tab).
 */
const draftFromEntry = async (params: URLSearchParams): Promise<BookingDraft> => {
  const today = dayjs().format("YYYY-MM-DD");
  const draft = emptyDraft(today);
  const type = params.get("type");
  const siteCode = params.get("site_code");
  const branchCode = params.get("site_branch_code");
  const branchName = params.get("site_branch_name");
  const recordCode = params.get("record_code");
  const date = params.get("date");

  if (type === "online" || type === "offline") {
    draft.online = type === "online";
  }

  if (siteCode && siteCode !== "null" && siteCode !== "undefined") {
    try {
      const resp = await siteService.getByCode(siteCode);

      draft.site = resp.data ?? { code: siteCode };
    } catch (error) {
      draft.site = { code: siteCode };
    }

    if (branchCode) {
      draft.branch = draft.site?.site_branch?.find((branch) => branch.code === branchCode) ?? { code: branchCode, name: branchName ?? "" };
    }
  }

  const doctorCode = params.get("doctor_code");
  const specializationCode = params.get("specialization_code");

  draft.preferredDoctorCode = doctorCode && doctorCode !== "undefined" ? doctorCode : null;
  draft.specializationFilter = specializationCode && specializationCode !== "undefined" && specializationCode !== "null" ? specializationCode : "";

  if (params.get("reappointment") === "1") {
    draft.skipServiceStep = true;

    if (date && date >= today) {
      draft.date = date;
    }

    if (recordCode) {
      try {
        const resp = await profileService.getByCode(recordCode);

        draft.record = resp.data?.patient_record ?? resp.data ?? null;
      } catch (error) {}
    }
  }

  return draft;
};

export default function AppointmentBooking() {
  //! state
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { handleCheckLogin } = useCheckLogin();
  const stepParam = searchParams.get("step") as BookingStep | null;
  const [draft, setDraft] = useState<BookingDraft | null>(() => (stepParam ? readDraft() : null));

  //! function
  useEffect(() => {
    handleCheckLogin();
  }, [handleCheckLogin]);

  // A new entry (no step yet) starts a fresh draft; a refresh mid-way keeps the one in progress.
  useEffect(() => {
    if (stepParam && draft) {
      return;
    }

    let ignore = false;

    draftFromEntry(searchParams).then((fresh) => {
      if (ignore) {
        return;
      }

      writeDraft(fresh);
      setDraft(fresh);
      setSearchParams({ step: nextStep(fresh) }, { replace: true });
    });

    return () => {
      ignore = true;
    };
  }, [stepParam, draft, searchParams, setSearchParams]);

  useEffect(() => {
    backToTop();
  }, [stepParam]);

  const update = useCallback((next: BookingDraft) => {
    writeDraft(next);
    setDraft(next);
  }, []);

  /**
   * Moves on to whatever is still missing - one history entry per step, so "back" goes back one step.
   * A change made from the booking screen returns to it by going back rather than stacking it again.
   */
  const goNext = useCallback(
    (next: BookingDraft, step?: BookingStep) => {
      const target = step ?? nextStep(next);

      update(next);

      if (target === "BOOKING" && next.reachedBooking && stepParam !== "BOOKING") {
        navigate(-1);
        return;
      }

      setSearchParams({ step: target });
    },
    [update, setSearchParams, navigate, stepParam],
  );

  const goTo = useCallback((step: BookingStep) => setSearchParams({ step }), [setSearchParams]);

  const done = useCallback(
    (path: string) => {
      sessionStorage.removeItem(BOOKING_DRAFT_KEY);
      navigate(path);
    },
    [navigate],
  );

  //! render
  const step = stepParam && STEPS.includes(stepParam) ? stepParam : null;

  const renderStep = () => {
    if (!draft || !step) {
      return <CommonStyles.LoadingUI />;
    }

    switch (step) {
      case "TYPE":
        return <ChooseTypeStep draft={draft} onNext={goNext} />;
      case "RECORD":
        return <ChooseRecordStep draft={draft} onNext={goNext} />;
      case "SITE":
        return <ChooseSiteStep draft={draft} onNext={goNext} />;
      case "SERVICE":
        return <ChooseServiceStep draft={draft} onNext={goNext} />;
      case "PAYMENT":
        return <PaymentStep draft={draft} onBack={() => navigate(-1)} onDone={done} />;
      default:
        return <BookingForm draft={draft} onChange={update} onGoTo={goTo} onNext={goNext} onDone={done} />;
    }
  };

  const title =
    step === "BOOKING" || step === "PAYMENT"
      ? draft?.online
        ? t("booking.onlineTitle")
        : t("booking.offlineTitle")
      : t(`booking.steps.${step ?? "TYPE"}`);

  return (
    <div className="bg-common-background min-h-[60vh]">
      <div className={`${responseUI.screen} ${responseUI.padding} py-6 lg:py-10`}>
        <div className="flex items-center gap-3 mb-4">
          {step && step !== "TYPE" && step !== "BOOKING" ? (
            <button type="button" aria-label={t("booking.back")} className="text-common-primary" onClick={() => navigate(-1)}>
              <CommonIcons.LeftOutlined />
            </button>
          ) : null}
          <h1 className="text-common-maintext text-xl font-semibold font-['Inter'] !m-0">{title}</h1>
        </div>

        <div className="bg-common-white rounded-2xl p-4 lg:p-8">{renderStep()}</div>
      </div>
    </div>
  );
}
